import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Empty, Input, Select, Table, Tooltip, message } from 'antd';
import dayjs from 'dayjs';
import { CalendarOff, Clock4, FilePlus2, Repeat2, Search, TimerReset } from 'lucide-react';
import Layout from '../../Layout';
import { useHr } from './hrStore';
import { tpApi, useTp } from './tpStore';
import { normText, toDay } from './hrLogic';
import { REQ_KIND, REQ_STATE, reqDate, reqSummary, shiftById, type ReqKind, type ReqState, type TimeReq } from './tpLogic';
import { Chip, EmpCell, Kpi, PageHeader, fmtDateTime } from './hrUi';
import { BranchSelect, ReqKindTag, ReqStateTag, RequestModal, askReason, done, useTpPerms } from './tpUi';

const KIND_ICON: Record<ReqKind, JSX.Element> = { leave: <CalendarOff size={18} />, explain: <TimerReset size={18} />, ot: <Clock4 size={18} />, swap: <Repeat2 size={18} /> };
const KIND_TONE: Record<ReqKind, string> = { leave: 'bg-violet-50 text-violet-600', explain: 'bg-orange-50 text-orange-600', ot: 'bg-sky-50 text-sky-600', swap: 'bg-teal-50 text-teal-600' };

/** Đơn từ: xin nghỉ, giải trình chấm công, làm thêm giờ, đổi ca — duyệt xong cập nhật thẳng bảng công / hồ sơ nhân sự */
export default function TimeRequests() {
  const s = useHr();
  const t = useTp();
  const { canApprove, canCreate, me } = useTpPerms();
  const [params, setParams] = useSearchParams();
  const go = (p: Record<string, string | null>) => {
    const n = new URLSearchParams(params);
    Object.entries(p).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k)));
    setParams(n, { replace: true });
  };
  const status = (params.get('status') || 'pending') as ReqState | 'all';
  const kind = (params.get('kind') || '') as ReqKind | '';
  const branchId = params.get('branch') || '';
  const q = params.get('q') || '';
  const [open, setOpen] = useState(params.get('new') === '1');
  const [sel, setSel] = useState<string[]>([]);
  const today = toDay();

  const emp = (id: string) => s.employees.find(e => e.id === id);
  const scoped = useMemo(() => t.requests.filter(r => {
    const e = emp(r.empId);
    return (!branchId || e?.branchId === branchId) && (!q || normText(`${e?.name} ${e?.code} ${r.code}`).includes(normText(q)));
  }), [t.requests, s.employees, branchId, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const list = scoped.filter(r => (status === 'all' || r.status === status) && (!kind || r.kind === kind))
    .sort((a, b) => (a.status === 'pending' ? 0 : 1) - (b.status === 'pending' ? 0 : 1) || reqDate(a).localeCompare(reqDate(b)) * (a.status === 'pending' ? 1 : -1));
  const pending = scoped.filter(r => r.status === 'pending');

  const approveMany = () => {
    let ok = 0;
    const errs: string[] = [];
    sel.forEach(id => { const r = tpApi.decide(id, true, me); if (r.ok) ok += 1; else errs.push(`${t.requests.find(x => x.id === id)?.code}: ${r.errors?.[0]}`); });
    if (ok) message.success(`Đã duyệt ${ok} đơn`);
    if (errs.length) done({ ok: false, errors: errs });
    setSel([]);
  };
  const reject = (r: TimeReq) => askReason(`Từ chối ${r.code}`, note => done(tpApi.decide(r.id, false, me, note), 'Đã từ chối'));
  const target = (r: TimeReq) => {
    const a = t.assigns.find(x => x.id === r.assignId);
    return a ? shiftById(t, a.shiftId)?.name : undefined;
  };

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Đơn từ" desc="Xin nghỉ, giải trình quên chấm, làm thêm giờ, đổi ca — đơn nghỉ được duyệt ghi thẳng vào hồ sơ nhân sự, giải trình được duyệt cập nhật giờ chấm"
          extra={canCreate && <Button type="primary" icon={<FilePlus2 size={14} />} onClick={() => setOpen(true)}>Tạo đơn</Button>} />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {(Object.keys(REQ_KIND) as ReqKind[]).map(k => (
            <Kpi key={k} icon={KIND_ICON[k]} tone={KIND_TONE[k]} label={`${REQ_KIND[k].label} chờ duyệt`} value={pending.filter(r => r.kind === k).length}
              onClick={() => go({ kind: kind === k ? null : k, status: null })} active={kind === k && status === 'pending'} />
          ))}
        </div>

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3 shadow-sm space-y-3">
          <div className="flex flex-wrap gap-2">
            <Input allowClear prefix={<Search size={14} className="text-gray-400" />} placeholder="Tên, mã NS, mã đơn" className="!w-[220px]" value={q} onChange={e => go({ q: e.target.value || null })} />
            <BranchSelect value={branchId} onChange={v => go({ branch: v || null })} />
            <Select className="w-[200px]" value={kind} onChange={v => go({ kind: v || null })} options={[{ value: '', label: 'Mọi loại đơn' }, ...(Object.keys(REQ_KIND) as ReqKind[]).map(k => ({ value: k, label: REQ_KIND[k].label }))]} />
            {canApprove && sel.length > 0 && <Button type="primary" onClick={approveMany}>Duyệt {sel.length} đơn</Button>}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(['pending', 'approved', 'rejected', 'cancelled'] as ReqState[]).map(st => (
              <Chip key={st} active={status === st} onClick={() => go({ status: st === 'pending' ? null : st })}>{REQ_STATE[st].label} · {scoped.filter(r => r.status === st && (!kind || r.kind === kind)).length}</Chip>
            ))}
            <Chip active={status === 'all'} onClick={() => go({ status: 'all' })}>Tất cả</Chip>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">
          <Table size="middle" rowKey="id" dataSource={list} scroll={{ x: 1100 }} pagination={{ pageSize: 20, showSizeChanger: false, showTotal: n => `${n} đơn` }}
            rowSelection={canApprove && status === 'pending' ? { selectedRowKeys: sel, onChange: k => setSel(k as string[]), getCheckboxProps: r => ({ disabled: r.status !== 'pending' }) } : undefined}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có đơn" /> }}
            columns={[
              { title: 'Đơn', key: 'code', width: 150, render: (_, r) => <div><div className="font-mono text-xs">{r.code}</div><ReqKindTag kind={r.kind} /></div> },
              { title: 'Nhân sự', key: 'e', width: 230, render: (_, r) => <EmpCell s={s} e={emp(r.empId)} /> },
              {
                title: 'Nội dung', key: 'sum', width: 300, render: (_, r) => (
                  <div className="text-sm">
                    <div>{reqSummary(r, target(r))}</div>
                    {r.kind === 'swap' && <div className="text-xs text-gray-500">Người làm thay: {emp(r.toEmpId!)?.name}</div>}
                    <div className="text-xs text-gray-400 truncate max-w-[300px]" title={r.reason}>{r.reason}</div>
                    {reqDate(r) < today && r.status === 'pending' && r.kind === 'leave' && <div className="text-[11px] text-amber-600">Ngày nghỉ đã qua</div>}
                  </div>
                ),
              },
              { title: 'Gửi lúc', key: 'at', width: 140, render: (_, r) => <div className="text-xs text-gray-500">{fmtDateTime(r.at)}<div>{r.by}</div></div> },
              {
                title: 'Trạng thái', key: 'st', width: 170, render: (_, r) => (
                  <div className="space-y-0.5">
                    <ReqStateTag r={r} />
                    {r.decidedBy && <div className="text-[11px] text-gray-400">{r.decidedBy} · {dayjs(r.decidedAt).format('DD/MM HH:mm')}</div>}
                    {r.decideNote && <Tooltip title={r.decideNote}><div className="text-[11px] text-rose-600 truncate max-w-[160px]">{r.decideNote}</div></Tooltip>}
                  </div>
                ),
              },
              {
                title: '', key: 'act', width: 190, render: (_, r) => (
                  <div className="flex flex-wrap gap-1">
                    {r.status === 'pending' && canApprove && <Button size="small" type="primary" onClick={() => done(tpApi.decide(r.id, true, me), `Đã duyệt ${r.code}`)}>Duyệt</Button>}
                    {r.status === 'pending' && canApprove && <Button size="small" danger onClick={() => reject(r)}>Từ chối</Button>}
                    {(r.status === 'pending' || (r.status === 'approved' && (r.kind === 'leave' || r.kind === 'ot'))) && (canApprove || r.by === me) && (
                      <Button size="small" type="text" onClick={() => done(tpApi.cancelRequest(r.id, me), 'Đã hủy đơn')}>Hủy</Button>
                    )}
                  </div>
                ),
              },
            ]} />
        </div>
      </div>
      {open && <RequestModal onClose={() => { setOpen(false); if (params.get('new')) go({ new: null }); }} />}
    </Layout>
  );
}
